import * as utils from './utils';
import smsConversationIcon from '../assets/images/smsConversationIcon.svg';
import webchatConversationIcon from '../assets/images/webchatConversationIcon.svg';

/**
 * @param {object} contact
 * @param {object} customerPhoneNumber
 * @return
 */

const attachCompanyName = (contactName, companyName) => {
  let modifiedContactName = contactName;

  if (companyName) {
    modifiedContactName = modifiedContactName
      ? `${modifiedContactName} (${companyName})`.trim()
      : `${companyName}`.trim();
  }

  return modifiedContactName;
};

export const getContactFullName = (contact, customerPhoneNumber) => {
  let contactName = null;

  if (contact) {
    if (contact?.DisplayName?.trim()) {
      contactName = contact.DisplayName.trim();
    } else if (
      contact.FirstName?.trim() ||
      contact.LastName?.trim() ||
      contact.CompanyName?.trim()
    ) {
      contactName = `${contact.FirstName || ''} ${contact.LastName || ''}`.trim();
      contactName = attachCompanyName(contactName, contact.CompanyName?.trim());
    } else if (
      contact.firstName?.trim() ||
      contact.lastName?.trim() ||
      contact.companyName?.trim()
    ) {
      contactName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
      contactName = attachCompanyName(contactName, contact.companyName?.trim());
    } else if (contact.CpUsername?.trim()) {
      contactName = contact.CpUsername.trim();
    } else if (contact.Emails?.length && contact.Emails[0]?.Email?.trim()) {
      contactName = contact.Emails[0].Email.trim();
    } else if (customerPhoneNumber) {
      contactName = customerPhoneNumber.Number;
    }
  }

  return contactName;
};

/**
 * @param {object} contact
 * @param {object} customerPhoneNumber
 * @return
 */
export const getContactNameForChannels = (contact, customerPhoneNumber = null) => {
  let contactName = null;
  if (contact) {
    if (contact.FirstName?.trim() || contact.LastName?.trim()) {
      contactName = `${contact.FirstName} ${contact.LastName ? contact.LastName[0] : ''}`.trim();
    } else if (contact.CpUsername?.trim()) {
      contactName = contact.CpUsername.trim();
    } else if (contact.Emails?.length && contact.Emails[0]?.Email?.trim()) {
      contactName = contact.Emails[0].Email.trim();
    } else if (customerPhoneNumber) {
      contactName = customerPhoneNumber.Number;
    }
  }

  return contactName;
};

/**
 * @param {object} contact
 * @return
 */
export const getContactInitials = (contact) => {
  let initials = '';

  if (contact) {
    const firstName = contact.FirstName?.trim() ? contact.FirstName[0] : '';

    const lastName = contact.LastName?.trim() ? contact.LastName[0] : '';

    initials = firstName + lastName;

    if (!initials.trim()) {
      initials = contact.CpUsername?.trim()?.[0] || contact.Emails?.[0]?.Email?.trim()?.[0] || '';
    }
  }

  return initials.trim() ? initials.toUpperCase() : '??';
};

export function getContactInitialsFromFullName(fullName) {
  let initials = '';
  const splitName = fullName.split(' ').filter(Boolean);

  if (splitName) {
    const firstName = splitName[0] && /[a-zA-Z]/.test(splitName[0][0]) ? splitName[0][0] : '';
    const lastName = splitName[1] && /[a-zA-Z]/.test(splitName[1][0]) ? splitName[1][0] : '';
    initials = (firstName + lastName).toUpperCase();
  }

  return initials && initials !== '' ? initials : '??';
}

export const getChannelIconFromContact = (contact) => {
  if (contact?.ContactType) {
    if (contact.ContactType.TypeId === utils.ContactTypeEnum.Customer.TypeId) {
      return smsConversationIcon;
    }

    if (contact.ContactType.TypeId === utils.ContactTypeEnum.WebChatContact.TypeId) {
      return webchatConversationIcon;
    }
  }

  return smsConversationIcon;
};

export const parseIncomingContacts = (incContacts, searchTerm, settings) => {
  if (!incContacts || !Array.isArray(incContacts) || !incContacts.length || !searchTerm) {
    return [];
  }

  // const settings = settingsStore.getLoggedUserSettings;

  return incContacts
    .reduce((allContacts, contact) => {
      if (
        contact.ContactType &&
        contact.ContactType.TypeId === utils.ContactTypeEnum.Customer.TypeId &&
        contact.PhoneNumbers.length
      ) {
        const rawPhoneNumber = searchTerm.replace(/[^\d+]/g, '');

        const filteredNumbers = contact.PhoneNumbers.filter(
          (f) => f?.Number?.includes(rawPhoneNumber) || f?.TwilioFormat?.includes(rawPhoneNumber)
        );

        const phoneNumbers = filteredNumbers.length === 0 ? contact.PhoneNumbers : filteredNumbers;

        for (let j = 0, numbersLen = phoneNumbers.length; j < numbersLen; j += 1) {
          const contactWithSingleNumber = {
            ...contact,
            PhoneNumbers: [phoneNumbers[j]],
          };
          allContacts.push(contactWithSingleNumber);
        }
      } else {
        allContacts.push(contact);
      }

      return allContacts;
    }, [])
    .map((contact, index) => {
      let label = '';

      if (contact.FirstName?.trim() || contact.LastName?.trim() || contact.CompanyName?.trim()) {
        label = getContactFullName(contact);
      } else if (contact.CpUsername?.trim()) {
        label = `${contact.CpUsername}`;
      } else if (contact.PhoneNumbers?.length) {
        label = `${contact.PhoneNumbers[0].Number}`;
      }

      return {
        label,
        value: label,
        data: incContacts.find((fullContact) => fullContact._id === contact._id),
        isChannel: false,
        selectedIndex: index,
        contactType: contact.ContactType ? contact.ContactType.TypeName : null,
        number: contact.PhoneNumbers.length ? `${contact.PhoneNumbers[0].Number}` : null,
        isSelected:
          settings.SelectedChannel &&
          settings.SelectedChannel.Contacts.some((cont) => cont._id === contact._id),
      };
    });
};

export const mapCustomerSearchResultsContacts = (incContacts, contactType) => {
  if (!incContacts || !Array.isArray(incContacts) || !incContacts.length || !contactType) {
    return [];
  }

  return incContacts.map((contact) => ({
    _id: utils.generateGuid(),
    FirstName: contact.firstName,
    LastName: contact.lastName,
    CompanyName: contact.companyName,
    ContactType: contactType,
    PhoneNumbers: contact.phones.map((phone) => ({
      _id: utils.generateGuid(),
      Number: utils.formatPhoneNumber(phone.number),
      Description: phone.description,
      TwilioFormat: `+1${phone.number}`,
    })),
    Address: null,
    IsFromPos: true,
  }));
};

export const getCustomerFromSmsChannel = (channel) =>
  channel?.ChannelType?.TypeId === utils.ChannelTypeEnum.SMS.TypeId
    ? channel?.Contacts?.find(
        (contact) =>
          contact.ContactType?.TypeId === utils.ContactTypeEnum.Customer.TypeId &&
          contact.PhoneNumbers.some((s) => s._id === channel.CustomerPhoneNumber?._id)
      )
    : null;
